/**
 * Session Manager for Frontend
 * Manages session with backend using cookies
 */
import { API_BASE_URL } from '../config';

interface SessionData {
  userId: string;
  userName: string;
  userFullName: string;
  canEdit?: boolean;
  expiresAt: string;
}

interface ValidationResponse {
  valid: boolean;
  user?: SessionData;
  message?: string;
}

class SessionManager {
  private static instance: SessionManager;

  private constructor() { }

  static getInstance(): SessionManager {
    if (!SessionManager.instance) {
      SessionManager.instance = new SessionManager();
    }
    return SessionManager.instance;
  }

  /**
   * Validate current session with backend
   */
  async validateSession(): Promise<ValidationResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/session`, {
        method: 'GET',
        credentials: 'include', // Include cookies
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        return {
          valid: data.valid,
          user: data.user
        };
      } else {
        const errorData = await response.json();
        return {
          valid: false,
          message: errorData.message || 'Session validation failed'
        };
      }
    } catch (error) {
      console.error('Session validation error:', error);
      return {
        valid: false,
        message: 'Failed to validate session'
      };
    }
  }

  /**
   * Authenticate with token from URL
   * This will create a session on the backend
   */
  async authenticateWithToken(token: string): Promise<ValidationResponse> {
    try {
      // Make a request to the session validation endpoint with the token in query params
      // The backend middleware will verify the token and create a session
      const response = await fetch(`${API_BASE_URL}/api/auth/session?token=${encodeURIComponent(token)}`, {
        method: 'GET',
        credentials: 'include', // Include cookies to receive session
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        return {
          valid: data.valid,
          user: data.user
        };
      } else {
        const errorData = await response.json();
        return {
          valid: false,
          message: errorData.message || 'Token authentication failed'
        };
      }
    } catch (error) {
      console.error('Token authentication error:', error);
      return {
        valid: false,
        message: 'Failed to authenticate with token'
      };
    }
  }

  /**
   * Logout and clear session
   */
  async logout(): Promise<void> {
    try {
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    const result = await this.validateSession();
    return result.valid;
  }

  /**
   * Get current user info
   */
  async getCurrentUser(): Promise<SessionData | null> {
    const result = await this.validateSession();
    return result.valid && result.user ? result.user : null;
  }
}

export default SessionManager.getInstance();
export type { SessionData, ValidationResponse };
